import { useEffect, useState } from 'react'
import { unlockVisit } from './api'
import AdminPage from './components/AdminPage'
import AuditorPrompts from './components/AuditorPrompts'
import ObservationEditor from './components/ObservationEditor'
import RecoveryPage from './components/RecoveryPage'
import { openingPrompts, templates } from './data/templates'
import { exportCsv, exportEvidenceZip, exportJson, printVisit } from './export'
import {
  changeTemplate,
  createObservation,
  createOpportunity,
  getVisitTemplate,
  getReviewWarnings,
  getVisitSummary,
  makeId,
  nowIso,
  removeAreaFromVisit,
} from './model'
import type {
  Area,
  CategoryTemplate,
  ClosingReview,
  Conflict,
  Opportunity,
  ReviewState,
  TemplateKind,
  VisitDetails,
  VisitSnapshot,
} from './types'
import { useAuditStore } from './useAuditStore'

type Tab = 'visit' | 'walk' | 'opportunities' | 'review'

const tabLabels: Record<Tab, string> = {
  visit: 'Visit',
  walk: 'Walk',
  opportunities: 'Candidates',
  review: 'Review',
}

const MTC_SLUG = 'the-manufacturing-technology-c-ansty-park-campus-e0c8c0605ce164da84e489034cd6ded9'

const syncLabels = {
  saving: 'Saving…',
  saved_local: 'Saved on this device',
  syncing: 'Syncing…',
  synced: 'Synced',
  offline: 'Offline · saved on device',
  conflict: 'Conflict needs review',
  storage_unavailable: 'Storage unavailable',
}

const opportunityStatusLabels: Record<Opportunity['developmentStatus'], string> = {
  candidate: 'Candidate',
  needs_data: 'Needs data',
  quantified: 'Quantified',
  assessor_reviewed: 'Assessor reviewed',
  approved: 'Approved',
}

function Brand() {
  return (
    <a className="brand" href="/" aria-label="Climatise">
      <img src="/climatise-logo.svg" alt="" />
      <span>Climatise</span>
    </a>
  )
}

function UnlockScreen({
  onUnlock,
}: {
  onUnlock: (contributor: string) => Promise<void>
}) {
  const [contributor, setContributor] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await onUnlock(contributor)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The visit could not be opened.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="unlock-page">
      <section className="unlock-card">
        <Brand />
        <div>
          <span className="eyebrow">Site visit checklist</span>
          <h1>Open the checklist</h1>
          <p>Enter your name so observations and changes can be attributed to you. Open each device online before travelling to site.</p>
        </div>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Your name
            <input
              autoComplete="name"
              value={contributor}
              onChange={(event) => setContributor(event.target.value)}
              placeholder="Used to label your observations"
              required
            />
          </label>
          {error && <p className="alert alert--danger" role="alert">{error}</p>}
          <button className="button button--wide" type="submit" disabled={busy}>
            {busy ? 'Opening…' : 'Open visit'}
          </button>
        </form>
      </section>
    </main>
  )
}

function Header({
  visit,
  syncState,
  pendingCount,
  lastSyncedAt,
  storageMode,
  onSync,
}: {
  visit: VisitSnapshot
  syncState: keyof typeof syncLabels
  pendingCount: number
  lastSyncedAt: string | null
  storageMode: 'device' | 'cloud' | 'unavailable'
  onSync: () => void
}) {
  const syncLabel = syncState === 'synced' && lastSyncedAt
    ? `${storageMode === 'cloud' ? 'Cloud saved' : 'Synced'} at ${new Date(lastSyncedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : storageMode === 'cloud' && syncState === 'saved_local'
      ? 'Saved · syncing'
      : syncLabels[syncState]
  return (
    <header className="app-header">
      <Brand />
      <div className="visit-identity">
        <strong>{visit.details.siteName || 'Untitled site'}</strong>
        <span>{visit.details.client || 'Client not set'}</span>
      </div>
      <button
        type="button"
        className={`sync-chip sync-chip--${syncState}`}
        onClick={onSync}
        title={pendingCount ? `${pendingCount} local item(s) waiting; automatic sync will retry` : 'Everything is up to date; syncing is automatic while online'}
      >
        <span className="sync-dot" aria-hidden="true" />
        {syncLabel}
        {pendingCount > 0 && <b>{pendingCount}</b>}
      </button>
    </header>
  )
}

function BottomNav({ tab, onChange }: { tab: Tab; onChange: (tab: Tab) => void }) {
  const items: Array<{ id: Tab; label: string; icon: string }> = [
    { id: 'visit', label: tabLabels.visit, icon: '⌂' },
    { id: 'walk', label: tabLabels.walk, icon: '☷' },
    { id: 'opportunities', label: tabLabels.opportunities, icon: '◇' },
    { id: 'review', label: tabLabels.review, icon: '✓' },
  ]
  return (
    <nav className="bottom-nav" aria-label="Visit sections">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={tab === item.id ? 'active' : ''}
          onClick={() => onChange(item.id)}
        >
          <span aria-hidden="true">{item.icon}</span>
          {item.label}
        </button>
      ))}
    </nav>
  )
}

function Stat({ value, label }: { value: number | string; label: string }) {
  return <div className="stat"><strong>{value}</strong><span>{label}</span></div>
}

function TextField({
  label,
  value,
  onChange,
  type = 'text',
  multiline = false,
  placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  multiline?: boolean
  placeholder?: string
}) {
  return (
    <label>
      {label}
      {multiline ? (
        <textarea rows={3} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input type={type} value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  )
}

function VisitScreen({
  visit,
  commit,
  onStartWalk,
}: {
  visit: VisitSnapshot
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
  onStartWalk: () => void
}) {
  const summary = getVisitSummary(visit)
  const updateDetail = (key: keyof VisitDetails, value: string) => void commit((current) => ({
    ...current,
    details: { ...current.details, [key]: value },
  }))
  const [newArea, setNewArea] = useState('')

  const addArea = () => {
    if (!newArea.trim()) return
    const area: Area = { id: makeId(), name: newArea.trim(), note: '', accessible: true }
    void commit((current) => ({ ...current, areas: [...current.areas, area] }))
    setNewArea('')
  }

  return (
    <div className="screen">
      <section className="screen-hero">
        <div>
          <span className="eyebrow">Physical site visit</span>
          <h1>{visit.details.siteName || 'Set up this visit'}</h1>
          <p>{visit.details.address || 'Add the site details and practical visit information before the walk.'}</p>
        </div>
        <button className="button" type="button" onClick={onStartWalk}>Start or resume walk</button>
      </section>

      <div className="stats-grid">
        <Stat value={summary.areas} label="Areas" />
        <Stat value={`${summary.reviewed}/${summary.categories}`} label="Categories reviewed" />
        <Stat value={summary.observations} label="Observations" />
        <Stat value={summary.opportunities} label="Opportunity candidates" />
      </div>

      <section className="panel">
        <div className="panel-heading">
          <div><span className="eyebrow">Known information</span><h2>Site and visit</h2></div>
          <span className="status-pill">Pre-fill or confirm</span>
        </div>
        <div className="field-grid field-grid--two">
          <TextField label="Client" value={visit.details.client} onChange={(value) => updateDetail('client', value)} />
          <TextField label="Site name" value={visit.details.siteName} onChange={(value) => updateDetail('siteName', value)} />
          <TextField label="Site address" value={visit.details.address} onChange={(value) => updateDetail('address', value)} multiline />
          <TextField label="Visit date" value={visit.details.visitDate} onChange={(value) => updateDetail('visitDate', value)} type="date" />
          <TextField label="Site contact and role" value={visit.details.siteContact} onChange={(value) => updateDetail('siteContact', value)} />
          <TextField label="Emergency contact" value={visit.details.emergencyContact} onChange={(value) => updateDetail('emergencyContact', value)} />
          <TextField label="Auditor(s)" value={visit.details.auditors} onChange={(value) => updateDetail('auditors', value)} />
          <label>
            Template
            {visit.slug === 'demo' ? (
              <select
                value={visit.templateId}
                disabled={visit.observations.length > 0}
                onChange={(event) => {
                  const value = event.target.value as TemplateKind
                  void commit((current) => changeTemplate(current, value))
                }}
              >
                {Object.values(templates).map((template) => <option key={template.id} value={template.id}>{template.label}</option>)}
              </select>
            ) : (
              <input value={`${getVisitTemplate(visit).label} · v${visit.templateVersion}`} readOnly />
            )}
            <small>{visit.slug === 'demo' ? 'Demo visits can switch template before observations start.' : 'Frozen when this visit was created; template updates affect new visits only.'}</small>
          </label>
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div><span className="eyebrow">Optional preparation</span><h2>Make the walk smoother</h2></div>
          <span className="status-pill">Client or staff</span>
        </div>
        <p className="section-intro">Only practical logistics and operational context belong here—no annual energy dataset or compliance workflow.</p>
        <div className="field-grid field-grid--two">
          <TextField label="Principal activities" value={visit.details.principalActivities} onChange={(value) => updateDetail('principalActivities', value)} multiline />
          <TextField label="Opening hours, shifts and expected activity" value={visit.details.operatingPattern} onChange={(value) => updateDetail('operatingPattern', value)} multiline />
          <TextField label="Induction, PPE, permits and hazards" value={visit.details.accessAndPpe} onChange={(value) => updateDetail('accessAndPpe', value)} multiline />
          <TextField label="Access, photography or recording restrictions" value={visit.details.restrictions} onChange={(value) => updateDetail('restrictions', value)} multiline />
          <TextField label="Known comfort, plant or recurring issues" value={visit.details.knownIssues} onChange={(value) => updateDetail('knownIssues', value)} multiline />
          <TextField label="Recent changes and planned replacements" value={visit.details.plannedChanges} onChange={(value) => updateDetail('plannedChanges', value)} multiline />
          <TextField label="People to interview" value={visit.details.peopleToInterview} onChange={(value) => updateDetail('peopleToInterview', value)} multiline />
          <TextField label="Maps, asset lists, schematics, BMS or O&M records available" value={visit.details.recordsAvailable} onChange={(value) => updateDetail('recordsAvailable', value)} multiline />
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div><span className="eyebrow">On arrival</span><h2>Actual visit conditions and opening discussion</h2></div>
          <span className="status-pill">Record on site</span>
        </div>
        <div className="field-grid field-grid--two">
          <TextField label="Weather and external conditions" value={visit.details.weather} onChange={(value) => updateDetail('weather', value)} />
          <TextField label="Actual occupancy, production or shift during the visit" value={visit.details.actualActivity} onChange={(value) => updateDetail('actualActivity', value)} />
          <TextField label="Outages, temporary conditions or constraints affecting what was observed" value={visit.details.visitConditions} onChange={(value) => updateDetail('visitConditions', value)} multiline />
        </div>
        <div className="opening-prompts">
          {openingPrompts.map((prompt, index) => {
            const id = `opening-${index + 1}`
            return (
              <TextField
                key={id}
                label={prompt}
                value={visit.openingResponses[id] ?? ''}
                multiline
                onChange={(value) => void commit((current) => ({
                  ...current,
                  openingResponses: { ...current.openingResponses, [id]: value },
                }))}
              />
            )
          })}
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div><span className="eyebrow">Walk route</span><h2>Areas and access</h2></div>
          <span className="status-pill">{visit.areas.length} areas</span>
        </div>
        <div className="inline-add">
          <input
            value={newArea}
            onChange={(event) => setNewArea(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') addArea() }}
            placeholder="Add area, e.g. Main plant room"
          />
          <button type="button" className="button" onClick={addArea}>Add area</button>
        </div>
        <div className="area-list">
          {visit.areas.map((area) => (
            <article className="area-row" key={area.id}>
              <input
                aria-label="Area name"
                value={area.name}
                onChange={(event) => {
                  const value = event.target.value
                  void commit((current) => ({
                    ...current,
                    areas: current.areas.map((item) => item.id === area.id ? { ...item, name: value } : item),
                  }))
                }}
              />
              <label className="switch-label">
                <input
                  type="checkbox"
                  checked={area.accessible}
                  onChange={(event) => {
                    const checked = event.target.checked
                    void commit((current) => ({
                      ...current,
                      areas: current.areas.map((item) => item.id === area.id ? { ...item, accessible: checked } : item),
                    }))
                  }}
                />
                Accessible
              </label>
              <button
                type="button"
                className="icon-button icon-button--danger area-row__remove"
                aria-label={`Remove area: ${area.name || 'unnamed area'}`}
                title="Remove area"
                onClick={() => {
                  const observationCount = visit.observations.filter((item) => item.areaId === area.id).length
                  const opportunityCount = visit.opportunities.filter((item) => item.areaIds.includes(area.id)).length
                  const linkedMessage = observationCount || opportunityCount
                    ? ` It is linked to ${observationCount} observation(s) and ${opportunityCount} opportunity item(s). Those records will be kept, but their link to this area will be cleared.`
                    : ''
                  if (window.confirm(`Remove “${area.name || 'this unnamed area'}”?${linkedMessage}`)) {
                    void commit((current) => removeAreaFromVisit(current, area.id))
                  }
                }}
              >
                ×
              </button>
              {!area.accessible && (
                <input
                  className="area-row__reason"
                  value={area.excludedReason ?? ''}
                  placeholder="Why was it not accessed?"
                  onChange={(event) => {
                    const value = event.target.value
                    void commit((current) => ({
                      ...current,
                      areas: current.areas.map((item) => item.id === area.id ? { ...item, excludedReason: value } : item),
                    }))
                  }}
                />
              )}
            </article>
          ))}
          {!visit.areas.length && <p className="empty-state">Add known areas now, or create them as you walk.</p>}
        </div>
      </section>
    </div>
  )
}

function CategoryCard({
  category,
  visit,
  active,
  onSelect,
}: {
  category: CategoryTemplate
  visit: VisitSnapshot
  active: boolean
  onSelect: () => void
}) {
  const progress = visit.categoryProgress[category.id]?.state ?? 'not_reviewed'
  const count = visit.observations.filter((item) => item.categoryId === category.id).length
  return (
    <button type="button" className={`category-card ${active ? 'active' : ''}`} onClick={onSelect}>
      <span className={`state-dot state-dot--${progress}`} />
      <span><strong>{category.label}</strong><small>{count} observation{count === 1 ? '' : 's'}</small></span>
      <span aria-hidden="true">›</span>
    </button>
  )
}

function WalkScreen({
  visit,
  commit,
  activeCategoryId,
  setActiveCategoryId,
  onEditObservation,
}: {
  visit: VisitSnapshot
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
  activeCategoryId: string
  setActiveCategoryId: (id: string) => void
  onEditObservation: (id: string) => void
}) {
  const template = getVisitTemplate(visit)
  const [query, setQuery] = useState('')
  const [showQuickChecks, setShowQuickChecks] = useState(false)
  const [jumpOpen, setJumpOpen] = useState(false)
  const [guidanceOpen, setGuidanceOpen] = useState(false)
  const [activeAreaId, setActiveAreaId] = useState(() => window.localStorage.getItem(`site-audit-area:${visit.id}`) ?? '')
  const [filter, setFilter] = useState<'all' | 'incomplete' | 'issue' | 'follow_up' | 'unsynced' | 'mine'>('all')
  const category = template.categories.find((item) => item.id === activeCategoryId) ?? template.categories[0]
  const matchesObservationFilter = (item: VisitSnapshot['observations'][number]) => {
    if (filter === 'issue') return item.condition === 'attention' || item.condition === 'poor' || Boolean(item.severity)
    if (filter === 'follow_up') return Boolean(item.followUp.trim())
    if (filter === 'unsynced') return item.evidenceIds.some((id) => visit.evidence.some((evidence) => evidence.id === id && evidence.localOnly))
    if (filter === 'mine') return item.createdBy === visit.contributorName
    return true
  }
  const categoryObservations = visit.observations.filter((item) => (
    item.categoryId === category.id
    && (!activeAreaId || item.areaId === activeAreaId)
    && matchesObservationFilter(item)
  ))
  const progress = visit.categoryProgress[category.id]

  const addObservation = async () => {
    const observation = createObservation(category.id, activeAreaId, visit.contributorName)
    await commit((current) => ({
      ...current,
      observations: [...current.observations, observation],
      categoryProgress: {
        ...current.categoryProgress,
        [category.id]: { state: 'in_progress', note: current.categoryProgress[category.id]?.note ?? '' },
      },
    }))
    onEditObservation(observation.id)
  }

  const visibleCategories = template.categories.filter((item) => {
    const textMatch = `${item.label} ${item.description}`.toLowerCase().includes(query.toLowerCase())
    if (!textMatch || filter === 'all') return textMatch
    if (filter === 'incomplete') {
      return ['not_reviewed', 'in_progress'].includes(visit.categoryProgress[item.id]?.state ?? 'not_reviewed')
    }
    return visit.observations.some((observation) => observation.categoryId === item.id && matchesObservationFilter(observation))
  })

  useEffect(() => {
    window.localStorage.setItem(`site-audit-area:${visit.id}`, activeAreaId)
  }, [activeAreaId, visit.id])

  return (
    <div className="walk-layout">
      <aside className="category-rail">
        <div className="rail-search">
          <input aria-label="Search categories" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Jump to a system…" />
          <select aria-label="Filter walk by area" value={activeAreaId} onChange={(event) => setActiveAreaId(event.target.value)}>
            <option value="">All areas</option>
            {visit.areas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}
          </select>
          <select aria-label="Filter walk items" value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}>
            <option value="all">All items</option>
            <option value="incomplete">Incomplete sections</option>
            <option value="issue">Issue found</option>
            <option value="follow_up">Follow-up</option>
            <option value="unsynced">Unsynced media</option>
            <option value="mine">My observations</option>
          </select>
        </div>
        <button type="button" className={`category-card category-card--checks ${showQuickChecks ? 'active' : ''}`} onClick={() => setShowQuickChecks(true)}>
          <span className="state-dot state-dot--in_progress" />
          <span><strong>Operational quick checks</strong><small>10 visible checks</small></span>
          <span aria-hidden="true">›</span>
        </button>
        {visibleCategories.map((item) => (
          <CategoryCard
            key={item.id}
            category={item}
            visit={visit}
            active={!showQuickChecks && item.id === category.id}
            onSelect={() => {
              setShowQuickChecks(false)
              setActiveCategoryId(item.id)
            }}
          />
        ))}
      </aside>

      <main className="walk-detail">
        <button type="button" className="mobile-jump-button" onClick={() => setJumpOpen(true)}>
          Jump to area or system
        </button>
        {showQuickChecks ? (
          <section className="panel panel--flush">
            <div className="panel-heading">
              <div><span className="eyebrow">Visible conditions</span><h1>Operational quick checks</h1></div>
            </div>
            <p className="section-intro">Answer only what you can see or establish on this visit. “Yes” means an issue or investigation point is present.</p>
            <div className="quick-checks">
              {template.quickChecks.map((item) => {
                const current = visit.quickChecks[item.id]
                return (
                  <article className="quick-check" key={item.id}>
                    <strong>{item.label}</strong>
                    <div className="segmented">
                      {(['yes', 'no', 'na'] as const).map((answer) => (
                        <button
                          type="button"
                          className={current?.answer === answer ? 'active' : ''}
                          onClick={() => void commit((snapshot) => ({
                            ...snapshot,
                            quickChecks: {
                              ...snapshot.quickChecks,
                              [item.id]: { ...snapshot.quickChecks[item.id], answer },
                            },
                          }))}
                          key={answer}
                        >
                          {answer === 'na' ? 'N/A' : answer[0].toUpperCase() + answer.slice(1)}
                        </button>
                      ))}
                    </div>
                    <input
                      value={current?.evidence ?? ''}
                      placeholder="Evidence, location or related observation"
                      onChange={(event) => {
                        const value = event.target.value
                        void commit((snapshot) => ({
                          ...snapshot,
                          quickChecks: {
                            ...snapshot.quickChecks,
                            [item.id]: { ...snapshot.quickChecks[item.id], evidence: value },
                          },
                        }))
                      }}
                    />
                  </article>
                )
              })}
            </div>
          </section>
        ) : (
          <section className="panel panel--flush">
            <div className="category-title">
              <div>
                <span className="eyebrow">System walk-round</span>
                <h1>{category.label}</h1>
                <p>{category.description}</p>
              </div>
              <label className="state-select">
                Section state
                <select
                  value={progress?.state ?? 'not_reviewed'}
                  onChange={(event) => {
                    const value = event.target.value as ReviewState
                    void commit((current) => ({
                      ...current,
                      categoryProgress: {
                        ...current.categoryProgress,
                        [category.id]: {
                          ...current.categoryProgress[category.id],
                          state: value,
                        },
                      },
                    }))
                  }}
                >
                  <option value="not_reviewed">Not reviewed</option>
                  <option value="in_progress">In progress</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="not_applicable">Not applicable</option>
                </select>
              </label>
            </div>
            {progress?.state === 'not_applicable' && (
              <label className="na-reason">
                Why is this not applicable?
                <input
                  value={progress.note}
                  onChange={(event) => {
                    const value = event.target.value
                    void commit((current) => ({
                      ...current,
                      categoryProgress: {
                        ...current.categoryProgress,
                        [category.id]: { ...current.categoryProgress[category.id], note: value },
                      },
                    }))
                  }}
                />
              </label>
            )}

            <AuditorPrompts
              category={category}
              open={guidanceOpen}
              onOpenChange={setGuidanceOpen}
            />

            <div className="observation-heading">
              <div><h2>Observations</h2><p>Short, factual records. Add only what you actually inspect.</p></div>
              <button type="button" className="button" onClick={() => void addObservation()}>＋ Add observation</button>
            </div>
            <div className="observation-list">
              {categoryObservations.map((observation) => (
                <button type="button" className="observation-card" onClick={() => onEditObservation(observation.id)} key={observation.id}>
                  <span className={`condition-marker condition-marker--${observation.condition}`} />
                  <span>
                    <strong>{observation.assetLabel || 'Untitled observation'}</strong>
                    <small>{visit.areas.find((area) => area.id === observation.areaId)?.name || 'No area'} · {observation.note || 'Add a note'}</small>
                  </span>
                  <span className="evidence-count">{observation.evidenceIds.length ? `${observation.evidenceIds.length} file${observation.evidenceIds.length === 1 ? '' : 's'}` : 'No media'}</span>
                  <span aria-hidden="true">›</span>
                </button>
              ))}
              {!categoryObservations.length && <p className="empty-state">No observations in this category yet.</p>}
            </div>
          </section>
        )}
      </main>
      {jumpOpen && (
        <div className="jump-overlay" role="dialog" aria-modal="true" aria-label="Jump to a system">
          <div className="jump-sheet">
            <div className="panel-heading">
              <div><span className="eyebrow">Walk navigation</span><h2>Jump to a system</h2></div>
              <button type="button" className="icon-button" onClick={() => setJumpOpen(false)}>×</button>
            </div>
            <input aria-label="Find a system" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search systems…" />
            <select className="jump-area-filter" aria-label="Choose walk area" value={activeAreaId} onChange={(event) => setActiveAreaId(event.target.value)}>
              <option value="">All areas</option>
              {visit.areas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}
            </select>
            <select className="jump-area-filter" aria-label="Filter systems" value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}>
              <option value="all">All items</option>
              <option value="incomplete">Incomplete sections</option>
              <option value="issue">Issue found</option>
              <option value="follow_up">Follow-up</option>
              <option value="unsynced">Unsynced media</option>
              <option value="mine">My observations</option>
            </select>
            <div className="jump-grid jump-grid--spaced">
              <button
                type="button"
                onClick={() => {
                  setShowQuickChecks(true)
                  setJumpOpen(false)
                }}
              >
                <strong>Operational quick checks</strong><span>Visible issues and constraints</span>
              </button>
              {visibleCategories.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => {
                    setActiveCategoryId(item.id)
                    setShowQuickChecks(false)
                    setJumpOpen(false)
                  }}
                >
                  <strong>{item.label}</strong><span>{item.description}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function OpportunityEditor({
  visit,
  opportunity,
  commit,
}: {
  visit: VisitSnapshot
  opportunity: Opportunity
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
}) {
  const template = getVisitTemplate(visit)
  const benefitOptions = ['Energy reduction', 'Cost reduction', 'Comfort', 'Reliability', 'Maintenance', 'Safety', 'Operational control', 'Carbon reduction']
  const update = (
    patch: Partial<Opportunity> | ((current: Opportunity) => Partial<Opportunity>),
  ) => commit((current) => ({
    ...current,
    opportunities: current.opportunities.map((item) => (
      item.id === opportunity.id
        ? { ...item, ...(typeof patch === 'function' ? patch(item) : patch), updatedAt: nowIso() }
        : item
    )),
  }))

  const categoryLabel = template.categories.find((category) => category.id === opportunity.categoryId)?.label ?? 'Other'

  return (
    <details className="opportunity-card">
      <summary className="opportunity-card__summary">
        <span>
          <strong>{opportunity.title || 'Untitled opportunity candidate'}</strong>
          <small>{categoryLabel} · {opportunity.observationIds.length} linked observation{opportunity.observationIds.length === 1 ? '' : 's'}</small>
        </span>
        <span className="status-pill">
          {opportunity.disposition === 'candidate'
            ? opportunityStatusLabels[opportunity.developmentStatus]
            : 'Not pursued'}
        </span>
      </summary>
      <div className="opportunity-card__body">
        <div className="opportunity-card__top">
        <input
          className="opportunity-title"
          value={opportunity.title}
          onChange={(event) => void update({ title: event.target.value })}
          placeholder="Opportunity title"
        />
        <button
          type="button"
          className="icon-button icon-button--danger"
          aria-label="Delete opportunity"
          onClick={() => {
            if (window.confirm('Delete this opportunity?')) {
              void commit((current) => ({
                ...current,
                opportunities: current.opportunities.filter((item) => item.id !== opportunity.id),
              }))
            }
          }}
        >
          ×
        </button>
        </div>
        <div className="field-grid field-grid--three">
        <label>
          System
          <select value={opportunity.categoryId} onChange={(event) => void update({ categoryId: event.target.value })}>
            {template.categories.map((category) => <option key={category.id} value={category.id}>{category.label}</option>)}
          </select>
        </label>
        <label>
          Priority
          <select value={opportunity.priority} onChange={(event) => void update({ priority: event.target.value as Opportunity['priority'] })}>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </label>
        <label>
          Confidence
          <select value={opportunity.confidence} onChange={(event) => void update({ confidence: event.target.value as Opportunity['confidence'] })}>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </label>
        </div>
        <label>What was observed?<textarea rows={2} value={opportunity.observed} onChange={(event) => void update({ observed: event.target.value })} /></label>
        <label>Suggested action<textarea rows={2} value={opportunity.recommendation} onChange={(event) => void update({ recommendation: event.target.value })} /></label>
        <fieldset>
        <legend>Areas or assets affected</legend>
        <div className="chip-grid">
          {visit.areas.map((area) => {
            const selected = opportunity.areaIds.includes(area.id)
            return (
              <label className={`choice-chip ${selected ? 'choice-chip--selected' : ''}`} key={area.id}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => void update((current) => ({
                    areaIds: current.areaIds.includes(area.id)
                      ? current.areaIds.filter((id) => id !== area.id)
                      : [...current.areaIds, area.id],
                  }))}
                />
                {area.name}
              </label>
            )
          })}
        </div>
        </fieldset>
        <fieldset>
        <legend>Likely benefit types</legend>
        <div className="chip-grid">
          {benefitOptions.map((benefit) => {
            const selected = opportunity.benefitTypes.includes(benefit)
            return (
              <label className={`choice-chip ${selected ? 'choice-chip--selected' : ''}`} key={benefit}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => void update((current) => ({
                    benefitTypes: current.benefitTypes.includes(benefit)
                      ? current.benefitTypes.filter((item) => item !== benefit)
                      : [...current.benefitTypes, benefit],
                  }))}
                />
                {benefit}
              </label>
            )
          })}
        </div>
        </fieldset>
        <details className="details-card">
        <summary>Evidence, constraints and follow-up</summary>
        <div className="details-card__body">
          <fieldset>
            <legend>Supporting observations</legend>
            <div className="linked-observations">
              {visit.observations.map((observation) => {
                const selected = opportunity.observationIds.includes(observation.id)
                return (
                  <label key={observation.id}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => void update((current) => ({
                        observationIds: current.observationIds.includes(observation.id)
                          ? current.observationIds.filter((id) => id !== observation.id)
                          : [...current.observationIds, observation.id],
                      }))}
                    />
                    <span>{observation.assetLabel || observation.note || 'Untitled observation'}</span>
                  </label>
                )
              })}
            </div>
          </fieldset>
          <div className="field-grid field-grid--two">
            <label>
              Implementation difficulty
              <select value={opportunity.difficulty} onChange={(event) => void update({ difficulty: event.target.value as Opportunity['difficulty'] })}>
                <option value="unknown">Unknown</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </label>
            <label>
              Field decision
              <select value={opportunity.disposition} onChange={(event) => void update({ disposition: event.target.value as Opportunity['disposition'] })}>
                <option value="candidate">Keep as candidate</option>
                <option value="considered_not_pursued">Considered, not pursued</option>
              </select>
            </label>
          </div>
          {opportunity.disposition === 'considered_not_pursued' && (
            <TextField label="Reason not pursued" value={opportunity.notPursuedReason} onChange={(value) => void update({ notPursuedReason: value })} multiline />
          )}
          <TextField label="Constraints or implementation notes" value={opportunity.constraints} onChange={(value) => void update({ constraints: value })} multiline />
          <TextField label="Data or supplier input still needed" value={opportunity.dataNeeded} onChange={(value) => void update({ dataNeeded: value })} multiline />
          <div className="field-grid field-grid--two">
            <TextField label="Responsible contact" value={opportunity.responsibleContact} onChange={(value) => void update({ responsibleContact: value })} />
            <TextField label="Target follow-up" value={opportunity.targetFollowUp} onChange={(value) => void update({ targetFollowUp: value })} type="date" />
          </div>
        </div>
        </details>
      </div>
    </details>
  )
}

function OpportunitiesScreen({
  visit,
  commit,
}: {
  visit: VisitSnapshot
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
}) {
  const add = () => {
    const opportunity = createOpportunity(getVisitTemplate(visit).categories[0].id, [], visit.contributorName)
    void commit((current) => ({ ...current, opportunities: [...current.opportunities, opportunity] }))
  }
  return (
    <div className="screen">
      <section className="screen-hero screen-hero--compact">
        <div>
          <span className="eyebrow">Optional field flags</span>
          <h1>Opportunity candidates</h1>
          <p>Observations are the primary site output. Flag only clear ideas for off-site development; calculations, costing and approval belong in the ESOS Opportunity Workbench.</p>
        </div>
        <button type="button" className="button" onClick={add}>＋ Flag candidate</button>
      </section>
      <div className="opportunity-list">
        {visit.opportunities.map((item) => <OpportunityEditor key={item.id} visit={visit} opportunity={item} commit={commit} />)}
        {!visit.opportunities.length && <div className="panel empty-state empty-state--large">No candidates flagged. That is fine—complete observations can be developed into detailed opportunities off site.</div>}
      </div>
    </div>
  )
}

const closingLabels: Record<keyof ClosingReview, string> = {
  findingsDiscussed: 'Key findings discussed with site contact',
  immediateActions: 'Immediate low/no-cost actions agreed',
  feasibilityProjects: 'Projects requiring feasibility or supplier input',
  informationToSupply: 'Information to be supplied after the visit',
  limitations: 'Disagreement, limitation or uncertainty',
  followUpContact: 'Follow-up contact',
  followUpDate: 'Follow-up date',
  mainSystems: 'Main energy-consuming systems observed',
  mainOpportunities: 'Most significant opportunities observed',
  operationalIssues: 'Operational issues or avoidable out-of-hours use',
  dataGaps: 'Data gaps for later work',
  nextSteps: 'Recommended next steps',
  auditorSignOff: 'Auditor sign-off / completion note',
  clientAcknowledgement: 'Optional client acknowledgement',
}

function ReviewScreen({
  visit,
  commit,
  onNavigate,
  onSync,
  conflicts,
  onResolveConflict,
}: {
  visit: VisitSnapshot
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
  onNavigate: (tab: Tab) => void
  onSync: () => void
  conflicts: Conflict[]
  onResolveConflict: (operationId: string, choice: 'local' | 'server') => void
}) {
  const warnings = getReviewWarnings(visit)
  const important = warnings.filter((item) => item.level === 'important').length
  const followUps = visit.observations.filter((item) => item.followUp.trim())
  const [exportError, setExportError] = useState('')
  const conflictFields = (localValue: unknown, cloudValue: unknown) => {
    const local = localValue && typeof localValue === 'object' ? localValue as Record<string, unknown> : { value: localValue }
    const cloud = cloudValue && typeof cloudValue === 'object' ? cloudValue as Record<string, unknown> : { value: cloudValue }
    return [...new Set([...Object.keys(local), ...Object.keys(cloud)])]
      .filter((key) => JSON.stringify(local[key]) !== JSON.stringify(cloud[key]))
      .slice(0, 12)
      .map((key) => ({
        key,
        local: typeof local[key] === 'string' ? local[key] : JSON.stringify(local[key] ?? null),
        cloud: typeof cloud[key] === 'string' ? cloud[key] : JSON.stringify(cloud[key] ?? null),
      }))
  }
  const currentConflictValue = (conflict: Conflict) => {
    if (conflict.operation.recordType === 'section') {
      if (conflict.operation.recordId === 'template') {
        return {
          templateId: visit.templateId,
          templateVersion: visit.templateVersion,
          templateSnapshot: visit.templateSnapshot,
        }
      }
      return visit[conflict.operation.recordId as keyof VisitSnapshot]
    }
    const collection = conflict.operation.recordType === 'observation'
      ? visit.observations
      : conflict.operation.recordType === 'opportunity'
        ? visit.opportunities
        : visit.evidence
    return collection.find((item) => item.id === conflict.operation.recordId) ?? null
  }
  const updateClosing = (key: keyof ClosingReview, value: string) => void commit((current) => ({
    ...current,
    closing: { ...current.closing, [key]: value },
  }))

  return (
    <div className="screen">
      <section className="screen-hero screen-hero--compact">
        <div>
          <span className="eyebrow">Final gap check</span>
          <h1>Review the visit</h1>
          <p>Warnings help you spot gaps; they do not block normal field capture.</p>
        </div>
        <span className={`review-score ${important ? 'review-score--warn' : ''}`}>
          {warnings.length ? `${warnings.length} check${warnings.length === 1 ? '' : 's'}` : 'Ready'}
        </span>
      </section>

      {conflicts.length > 0 && (
        <section className="panel conflict-panel">
          <div className="panel-heading">
            <div><span className="eyebrow">Collaboration conflict</span><h2>Choose which edit to keep</h2></div>
          </div>
          <p className="section-intro">Another device changed the visit before this device synced. Nothing is discarded until you choose.</p>
          {conflicts.map((conflict) => (
            <div className="conflict-row" key={conflict.operation.id}>
              <span>Local edit by <strong>{conflict.operation.author}</strong> conflicts with server revision {conflict.serverRevision}.</span>
              <details>
                <summary>Compare changed fields</summary>
                <div className="conflict-diff">
                  {conflictFields(currentConflictValue(conflict), conflict.serverRecord).map((field) => (
                    <div key={field.key}>
                      <strong>{field.key}</strong>
                      <span><b>This device:</b> {field.local || 'Blank'}</span>
                      <span><b>Cloud:</b> {field.cloud || 'Blank'}</span>
                    </div>
                  ))}
                </div>
              </details>
              <div className="button-row">
                <button type="button" className="button button--secondary" onClick={() => onResolveConflict(conflict.operation.id, 'server')}>Use cloud copy</button>
                <button type="button" className="button" onClick={() => onResolveConflict(conflict.operation.id, 'local')}>Keep this device</button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="panel">
        <div className="panel-heading"><div><h2>Checks</h2><p>{important} important item{important === 1 ? '' : 's'}</p></div></div>
        <div className="warning-list">
          {warnings.map((warning) => (
            <button type="button" key={warning.id} onClick={() => onNavigate(warning.target)}>
              <span className={`warning-icon warning-icon--${warning.level}`}>{warning.level === 'important' ? '!' : '?'}</span>
              <span><strong>{warning.title}</strong><small>{warning.detail}</small></span>
              <span aria-hidden="true">›</span>
            </button>
          ))}
          {!warnings.length && <p className="empty-state">No checklist gaps found. Confirm the close-out and sync before leaving.</p>}
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div><span className="eyebrow">Actions after the walk</span><h2>Follow-ups</h2></div>
          <span className="status-pill">{followUps.length}</span>
        </div>
        <div className="follow-up-list">
          {followUps.map((item) => (
            <article key={item.id}>
              <div>
                <strong>{item.assetLabel || item.note || 'Observation follow-up'}</strong>
                <span>{item.followUp}</span>
              </div>
              <small>{item.followUpOwner || 'No owner'}{item.followUpDate ? ` · ${item.followUpDate}` : ''}</small>
            </article>
          ))}
          {!followUps.length && <p className="empty-state">No follow-up actions recorded.</p>}
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading"><div><span className="eyebrow">Close-out</span><h2>Discussion and summary</h2></div></div>
        <div className="field-grid field-grid--two">
          {(Object.keys(closingLabels) as Array<keyof ClosingReview>).map((key) => (
            <TextField
              key={key}
              label={closingLabels[key]}
              value={visit.closing[key]}
              onChange={(value) => updateClosing(key, value)}
              type={key === 'followUpDate' ? 'date' : 'text'}
              multiline={key !== 'followUpDate' && key !== 'followUpContact'}
            />
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading"><div><span className="eyebrow">Standalone record</span><h2>Sync and export</h2></div></div>
        <div className="export-grid">
          <button type="button" onClick={onSync}><strong>Sync now</strong><span>Sync is automatic while online. Tap here to retry or confirm before leaving site.</span></button>
          <button type="button" onClick={() => {
            setExportError('')
            void printVisit(visit).catch((error: unknown) => setExportError(error instanceof Error ? error.message : 'PDF preparation failed.'))
          }}><strong>Print / PDF</strong><span>Open a polished site-visit record for printing or PDF.</span></button>
          <button type="button" onClick={() => {
            setExportError('')
            void exportEvidenceZip(visit).catch((error: unknown) => setExportError(error instanceof Error ? error.message : 'Evidence export failed.'))
          }}><strong>Workbench package</strong><span>Download the complete visit, media and candidate handoff for off-site opportunity development.</span></button>
          <button type="button" onClick={() => exportCsv(visit)}><strong>Observation CSV</strong><span>Portable internal observation list.</span></button>
          <button type="button" onClick={() => exportJson(visit)}><strong>JSON backup</strong><span>Complete structured visit snapshot.</span></button>
        </div>
        {exportError && <p className="alert alert--danger export-error" role="alert">{exportError}</p>}
      </section>
    </div>
  )
}

function QuickCapture({
  visit,
  commit,
  recentCategoryId,
  onEdit,
}: {
  visit: VisitSnapshot
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
  recentCategoryId: string
  onEdit: (id: string, categoryId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const template = getVisitTemplate(visit)
  const recentCategory = template.categories.find((category) => category.id === recentCategoryId)
    ?? template.categories[0]
  const storedAreaId = window.localStorage.getItem(`site-audit-area:${visit.id}`) ?? ''
  const recentArea = visit.areas.find((area) => area.id === storedAreaId)
  const create = async (categoryId: string) => {
    const areaId = visit.areas.some((area) => area.id === storedAreaId) ? storedAreaId : ''
    const observation = createObservation(categoryId, areaId, visit.contributorName)
    await commit((current) => ({
      ...current,
      observations: [...current.observations, observation],
      categoryProgress: {
        ...current.categoryProgress,
        [categoryId]: { ...current.categoryProgress[categoryId], state: 'in_progress' },
      },
    }))
    setOpen(false)
    onEdit(observation.id, categoryId)
  }
  return (
    <>
      <button type="button" className="quick-capture" onClick={() => setOpen(true)}><span>＋</span> Quick capture</button>
      {open && (
        <div className="jump-overlay" role="dialog" aria-modal="true" aria-label="Choose a category">
          <div className="jump-sheet">
            <div className="panel-heading">
              <div><span className="eyebrow">Quick capture</span><h2>Where does this belong?</h2><p>The most recent system and area are ready to reuse.</p></div>
              <button type="button" className="icon-button" onClick={() => setOpen(false)}>×</button>
            </div>
            <button type="button" className="recent-capture" onClick={() => void create(recentCategory.id)}>
              <span>Continue in</span>
              <strong>{recentCategory.label}</strong>
              <small>{recentArea ? recentArea.name : 'No area selected yet'}</small>
            </button>
            <div className="jump-grid">
              {template.categories.filter((category) => category.id !== recentCategory.id).map((category) => (
                <button type="button" key={category.id} onClick={() => void create(category.id)}>
                  <strong>{category.label}</strong><span>{category.description}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function AuditApp({ slug, onLock }: { slug: string; onLock: () => void }) {
  const store = useAuditStore(slug)
  const workspaceKey = `site-audit-workspace:${slug}`
  const [tab, setTab] = useState<Tab>(() => {
    try {
      return (JSON.parse(window.localStorage.getItem(workspaceKey) || '{}') as { tab?: Tab }).tab ?? 'visit'
    } catch {
      return 'visit'
    }
  })
  const [activeCategoryId, setActiveCategoryId] = useState(() => {
    try {
      return (JSON.parse(window.localStorage.getItem(workspaceKey) || '{}') as { categoryId?: string }).categoryId ?? ''
    } catch {
      return ''
    }
  })
  const [editingObservationId, setEditingObservationId] = useState<string | null>(null)

  useEffect(() => {
    void store.initialise(true)
    // Store is intentionally initialised once for this slug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  useEffect(() => {
    if (store.authExpired) onLock()
  }, [onLock, store.authExpired])

  useEffect(() => {
    window.localStorage.setItem(workspaceKey, JSON.stringify({ tab, categoryId: activeCategoryId }))
  }, [activeCategoryId, tab, workspaceKey])

  if (!store.ready) return <main className="loading-page"><Brand /><p>Opening the saved visit…</p></main>
  if (store.storageMode === 'unavailable') {
    return (
      <main className="unlock-page">
        <section className="unlock-card">
          <Brand />
          <h1>Safe saving is unavailable</h1>
          <p>No edits have been accepted. Keep this page open, check the connection and try again before recording audit information.</p>
          <button className="button" type="button" onClick={() => window.location.reload()}>Try again</button>
        </section>
      </main>
    )
  }
  if (!store.visit) {
    return (
      <main className="unlock-page">
        <section className="unlock-card">
          <Brand />
          <h1>Visit unavailable</h1>
          <p>The visit could not be downloaded and no offline copy exists on this device.</p>
          <button className="button" type="button" onClick={onLock}>Open again</button>
        </section>
      </main>
    )
  }

  const visit = store.visit
  const template = getVisitTemplate(visit)
  const resolvedCategoryId = activeCategoryId || template.categories[0].id
  const editingObservation = visit.observations.find((item) => item.id === editingObservationId)

  return (
    <div className="app-shell">
      <Header
        visit={visit}
        syncState={store.syncState}
        pendingCount={store.pendingCount}
        lastSyncedAt={store.lastSyncedAt}
        storageMode={store.storageMode}
        onSync={() => void store.syncNow()}
      />
      {slug === MTC_SLUG && (
        <div className="safety-release-banner" role="status">
          <strong>28 Oct safety update active</strong>
          <span>Use one device at a time and wait for Synced before closing or switching.</span>
        </div>
      )}
      {store.storageMode === 'cloud' && (
        <div className="cloud-mode-banner" role="status">
          <strong>Cloud-save mode</strong>
          <span>Notes save automatically. Keep a connection for photos, voice notes and sketches.</span>
        </div>
      )}
      <div className="desktop-tabs">
        {(['visit', 'walk', 'opportunities', 'review'] as Tab[]).map((item) => (
          <button type="button" className={tab === item ? 'active' : ''} onClick={() => setTab(item)} key={item}>
            {tabLabels[item]}
          </button>
        ))}
      </div>

      {tab === 'visit' && <VisitScreen visit={visit} commit={store.commit} onStartWalk={() => setTab('walk')} />}
      {tab === 'walk' && (
        <WalkScreen
          visit={visit}
          commit={store.commit}
          activeCategoryId={resolvedCategoryId}
          setActiveCategoryId={setActiveCategoryId}
          onEditObservation={setEditingObservationId}
        />
      )}
      {tab === 'opportunities' && <OpportunitiesScreen visit={visit} commit={store.commit} />}
      {tab === 'review' && (
        <ReviewScreen
          visit={visit}
          commit={store.commit}
          onNavigate={setTab}
          onSync={() => void store.syncNow()}
          conflicts={store.conflicts}
          onResolveConflict={(operationId, choice) => void store.resolveConflict(operationId, choice)}
        />
      )}

      <QuickCapture
        visit={visit}
        commit={store.commit}
        recentCategoryId={resolvedCategoryId}
        onEdit={(id, categoryId) => {
          setActiveCategoryId(categoryId)
          setTab('walk')
          setEditingObservationId(id)
        }}
      />
      <BottomNav tab={tab} onChange={setTab} />

      {editingObservation && (
        <ObservationEditor
          visit={visit}
          observation={editingObservation}
          commit={store.commit}
          onClose={() => setEditingObservationId(null)}
          onCategoryChange={setActiveCategoryId}
          storageMode={store.storageMode}
        />
      )}
    </div>
  )
}

function VisitEntry({ slug }: { slug: string }) {
  const sessionKey = `site-audit-unlocked:${slug}`
  const grantKey = `site-audit-offline-grant:${slug}`
  const [unlocked, setUnlocked] = useState(() => {
    if (window.sessionStorage.getItem(sessionKey) === 'yes') return true
    try {
      const grant = JSON.parse(window.localStorage.getItem(grantKey) || '{}') as { expiresAt?: string }
      return Boolean(grant.expiresAt && new Date(grant.expiresAt).getTime() > Date.now())
    } catch {
      return false
    }
  })

  const unlock = async (contributor: string) => {
    let expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    if (slug !== 'demo') {
      const session = await unlockVisit(slug, contributor)
      expiresAt = session.expiresAt
    }
    window.localStorage.setItem(`site-audit-contributor:${slug}`, contributor)
    window.localStorage.setItem('site-audit-last-slug', slug)
    window.localStorage.setItem(grantKey, JSON.stringify({ expiresAt }))
    window.sessionStorage.setItem(sessionKey, 'yes')
    setUnlocked(true)
  }

  if (!unlocked) return <UnlockScreen onUnlock={unlock} />
  return (
    <AuditApp
      slug={slug}
      onLock={() => {
        window.sessionStorage.removeItem(sessionKey)
        window.localStorage.removeItem(grantKey)
        setUnlocked(false)
      }}
    />
  )
}

export default function App() {
  if (window.location.pathname === '/recovery') return <RecoveryPage />
  if (window.location.pathname.startsWith('/admin')) return <AdminPage />
  const pathSlug = window.location.pathname.startsWith('/visit/')
    ? window.location.pathname.split('/').filter(Boolean).at(-1)
    : null
  const slug = pathSlug || window.localStorage.getItem('site-audit-last-slug') || 'demo'
  if (!pathSlug && slug !== 'demo') window.history.replaceState({}, '', `/visit/${slug}`)
  return <VisitEntry slug={slug} />
}
